import { IActivityStats, IActivityStatsFilters } from '@sdk/contracts';
import { useQuery } from '@tanstack/react-query';
import { SorterResult } from 'antd/lib/table/interface';
import { useParams } from 'react-router-dom';
import { create as createStore } from 'zustand';

import { sdk } from '@/services';

const INITIAL_PAGE = 1;
const INITIAL_LIMIT = 50;

const ActivityStatsService = sdk.getService('ActivityStatsService');

interface IStore {
  page: number;
  limit: number;
  search: string;
  sort?: SorterResult<IActivityStats>;
  filters?: IActivityStatsFilters;
  setFilters: (filters: IActivityStatsFilters) => void;
  setSearch: (search: string) => void;
  setPageAndLimit: (page: number, limit: number) => void;
  setSort: (sort: SorterResult<IActivityStats>) => void;
}

const store = createStore<IStore>(set => ({
  page: INITIAL_PAGE,
  limit: INITIAL_LIMIT,
  search: '',
  setFilters: filters => set(state => ({ ...state, filters })),
  setSearch: (search: string) => set(state => ({ ...state, search, page: INITIAL_PAGE })),
  setPageAndLimit: (page: number, limit: number) => set(state => ({ ...state, page, limit })),
  setSort: (sort: SorterResult<IActivityStats>) => set(state => ({ ...state, sort }))
}));

export const useActivityStatsList = () => {
  const { page, limit, search, setSearch, setPageAndLimit, sort, setSort, filters, setFilters } =
    store();
  const { _id } = useParams<{ _id: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ['stats', 'activity', _id, `page-${page}`, `limit-${limit}`, search, sort, filters],
    queryFn: () =>
      ActivityStatsService.getActivityStats(_id!, search, { page, limit }, sort, filters),
    enabled: !!_id
  });

  const pagination = data && {
    page: data.page,
    limit: data.limit,
    hasNextPage: data.hasNextPage,
    hasPrevPage: data.hasPrevPage,
    nextPage: data.nextPage,
    pagingCounter: data.pagingCounter,
    prevPage: data.prevPage,
    totalDocs: data.totalDocs,
    totalPages: data.totalPages
  };

  return {
    activityStatsList: data?.docs,
    isLoading,
    pagination,
    search,
    sort,
    filters,
    setPageAndLimit,
    setSearch,
    setSort,
    setFilters
  };
};
